import { useMemo, useState } from 'react'
import {
  Box, Grid, Card, CardContent, Typography, Chip,
  Stack, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Switch, FormControlLabel, Alert,
  IconButton, Tooltip, Table, TableHead, TableRow, TableCell, TableBody,
  Tabs, Tab,
} from '@mui/material'
import { Add, Edit, Delete, TrendingUp, AttachMoney, AccountBalanceWallet, Payment, History } from '@mui/icons-material'
import { MenuItem } from '@mui/material'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useAdminTenants } from '../../hooks/usePlatformAdmin'
import {
  usePaymentGateways, useCreateGateway, useUpdateGateway, useDeleteGateway,
  useAdminWallets, useWalletTopup, useWalletTransactions,
  usePricingTiers, useCreatePricingTier, useUpdatePricingTier, useDeletePricingTier,
  useAdminPayments, useAdminSubscribeTenant, useAdminSubscriptions,
  type PaymentGateway, type PricingTier,
} from '../../hooks/usePlatformAdmin'
import { AdminDataError } from '../../components/AdminDataError'
import { AdminPageHeader } from '../../components/AdminPageHeader'
import { AdminLoadingState } from '../../components/AdminLoadingState'

type Tenant = { id: string; name: string; status: string; currency?: string }

const emptyGateway: Omit<PaymentGateway, 'id' | 'created_at' | 'updated_at'> = {
  name: '', provider: '', currency: 'USD', fee_type: 'percentage', fee_value: 0, is_active: true, config: '{}',
}

function KpiCard({ label, value, sub, icon, iconBg, iconColor }: {
  label: string; value: string | number; sub?: string; icon: React.ReactNode; iconBg: string; iconColor: string
}) {
  return (
    <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {label}
            </Typography>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#0f172a', mt: 0.5, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              {value}
            </Typography>
            {sub && <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.25, display: 'block' }}>{sub}</Typography>}
          </Box>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ color: iconColor, display: 'flex' }}>{icon}</Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}


export default function AdminBillingPage() {
  const tenantsQuery = useAdminTenants()
  const gatewaysQuery = usePaymentGateways()
  const walletsQuery = useAdminWallets()
  const { data: tenants = [], isLoading: tenantsLoading } = tenantsQuery
  const { data: gateways = [] } = gatewaysQuery
  const { data: wallets = [] } = walletsQuery
  const createGateway = useCreateGateway()
  const updateGateway = useUpdateGateway()
  const deleteGateway = useDeleteGateway()
  const topupWallet   = useWalletTopup()

  const [gwDialogOpen, setGwDialogOpen]       = useState(false)
  const [editingGw, setEditingGw]             = useState<PaymentGateway | null>(null)
  const [gwForm, setGwForm]                   = useState<Omit<PaymentGateway, 'id' | 'created_at' | 'updated_at'>>(emptyGateway)
  const [deleteGwTarget, setDeleteGwTarget]   = useState<string | null>(null)

  const [topupDialogOpen, setTopupDialogOpen] = useState(false)
  const [topupTenantId, setTopupTenantId]     = useState('')
  const [topupForm, setTopupForm]             = useState({ amount: '', description: '' })

  const [historyTenantId, setHistoryTenantId] = useState<string | null>(null)
  const [billingSection, setBillingSection] = useState<'pricing' | 'gateways' | 'wallets' | 'transactions'>('pricing')

  // Pricing tiers
  const tiersQuery = usePricingTiers()
  const { data: tiers = [] } = tiersQuery
  const createTier = useCreatePricingTier()
  const updateTier = useUpdatePricingTier()
  const deleteTier = useDeletePricingTier()
  const [tierDialogOpen, setTierDialogOpen] = useState(false)
  const [editingTier, setEditingTier] = useState<PricingTier | null>(null)
  const [tierForm, setTierForm] = useState({ name: '', min_devices: 1, max_devices: '' as string | number, price_per_device_monthly: 0, yearly_discount_pct: 0, is_active: true, sort_order: 0 })
  const [deleteTierTarget, setDeleteTierTarget] = useState<string | null>(null)

  // Admin payments + subscribe tenant
  const adminPaymentsQuery = useAdminPayments()
  const subscriptionsQuery = useAdminSubscriptions()
  const { data: adminPayments = [] } = adminPaymentsQuery
  const { data: subscriptions = [] } = subscriptionsQuery
  const subscribeTenant = useAdminSubscribeTenant()
  const [subTenantDialog, setSubTenantDialog] = useState(false)
  const [subTenantForm, setSubTenantForm] = useState({ tenant_id: '', device_count: '5', billing_cycle: 'monthly', amount: '', description: '' })
  const { data: txHistory = [] }              = useWalletTransactions(historyTenantId ?? '')

  const payingTenants = new Set(
    subscriptions.filter((s) => s.status === 'active' || s.status === 'grace').map((s) => s.tenant_id),
  )
  const walletByTenant = useMemo(() => new Map(wallets.map((w) => [w.tenant_id, w])), [wallets])
  const gatewayConfigError = (() => {
    try { JSON.parse(gwForm.config || '{}'); return '' } catch { return 'Configuration must be valid JSON' }
  })()
  const tierMax = tierForm.max_devices === '' ? null : Number(tierForm.max_devices)
  const tierFormValid = !!tierForm.name.trim() &&
    tierForm.min_devices >= 1 &&
    (tierMax === null || tierMax >= tierForm.min_devices) &&
    tierForm.price_per_device_monthly >= 0 &&
    tierForm.yearly_discount_pct >= 0 && tierForm.yearly_discount_pct <= 100

  const openCreateGw = () => { setEditingGw(null); setGwForm(emptyGateway); setGwDialogOpen(true) }
  const openEditGw   = (g: PaymentGateway) => {
    setEditingGw(g)
    setGwForm({ name: g.name, provider: g.provider, currency: g.currency, fee_type: g.fee_type, fee_value: g.fee_value, is_active: g.is_active, config: g.config })
    setGwDialogOpen(true)
  }
  const handleGwSubmit = async () => {
    try {
      if (editingGw) { await updateGateway.mutateAsync({ ...editingGw, ...gwForm }) }
      else           { await createGateway.mutateAsync(gwForm) }
      setGwDialogOpen(false)
    } catch { /* API interceptor shows the failure */ }
  }

  const openTopup = (tenantId: string) => { setTopupTenantId(tenantId); setTopupForm({ amount: '', description: '' }); setTopupDialogOpen(true) }
  const handleTopup = async () => {
    const tenant = (tenants as Tenant[]).find((t) => t.id === topupTenantId)
    try {
      await topupWallet.mutateAsync({
        tenantId: topupTenantId,
        amount: parseFloat(topupForm.amount),
        description: topupForm.description.trim() || 'Admin wallet top-up',
        currency: tenant?.currency || 'USD',
      })
      setTopupDialogOpen(false)
    } catch { /* API interceptor shows the failure */ }
  }

  if (tenantsLoading) {
    return <AdminLoadingState />
  }

  return (
    <Box>
      <AdminPageHeader
        eyebrow="Revenue operations"
        title="Billing & Subscriptions"
        description="Configure pricing, payment gateways, tenant balances, and manual subscriptions from one workspace."
        icon={<Payment />}
      />

      {(tenantsQuery.isError || gatewaysQuery.isError || walletsQuery.isError || tiersQuery.isError || adminPaymentsQuery.isError || subscriptionsQuery.isError) && (
        <AdminDataError
          message="Some billing data could not be loaded. Totals may be incomplete."
          onRetry={() => {
            void tenantsQuery.refetch()
            void gatewaysQuery.refetch()
            void walletsQuery.refetch()
            void tiersQuery.refetch()
            void adminPaymentsQuery.refetch()
            void subscriptionsQuery.refetch()
          }}
        />
      )}

      {/* KPI row */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Paying Tenants" value={payingTenants.size} sub="active or grace subscriptions"
            icon={<TrendingUp />} iconBg="#EBF6FD" iconColor="#34A8E0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Payment Gateways" value={gateways.filter(g => g.is_active).length} sub={`of ${gateways.length} total`}
            icon={<Payment />} iconBg="#f0fdf4" iconColor="#10b981" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Pricing Tiers" value={tiers.filter(t => t.is_active).length} sub="active tiers"
            icon={<AttachMoney />} iconBg="#eff6ff" iconColor="#3b82f6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Wallets" value={wallets.length} sub={`of ${tenants.length} tenants initialized`}
            icon={<AccountBalanceWallet />} iconBg="#fff7ed" iconColor="#f97316" />
        </Grid>
      </Grid>

      <Card sx={{ overflow: 'hidden' }}>
        <Tabs
          value={billingSection}
          onChange={(_, value) => setBillingSection(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Billing workspace sections"
        >
          <Tab value="pricing" icon={<AttachMoney />} iconPosition="start" label="Pricing" />
          <Tab value="gateways" icon={<Payment />} iconPosition="start" label="Gateways" />
          <Tab value="wallets" icon={<AccountBalanceWallet />} iconPosition="start" label="Wallets" />
          <Tab value="transactions" icon={<History />} iconPosition="start" label="Transactions" />
        </Tabs>
      </Card>

      {/* ── Pricing Tiers (Per-Device) ──────────────────────────── */}
      <Box sx={{ mt: 3, display: billingSection === 'pricing' ? 'block' : 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney sx={{ color: '#34A8E0', fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15 }}>
              Per-Device Pricing Tiers
            </Typography>
          </Box>
          <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => {
            setEditingTier(null)
            setTierForm({ name: '', min_devices: 1, max_devices: '', price_per_device_monthly: 0, yearly_discount_pct: 0, is_active: true, sort_order: 0 })
            setTierDialogOpen(true)
          }}>Add Tier</Button>
        </Box>
        <Card sx={{ bgcolor: '#ffffff', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Tier</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Devices</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>$/device/mo</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Yearly Discount</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#475569' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: '#94a3b8', py: 3 }}>No pricing tiers configured</TableCell></TableRow>
              )}
              {tiers.map((t) => (
                <TableRow key={t.id}>
                  <TableCell><Typography fontWeight={600}>{t.name}</Typography></TableCell>
                  <TableCell>{t.min_devices}–{t.max_devices ?? '∞'}</TableCell>
                  <TableCell><Typography fontWeight={700} color="#34A8E0">${t.price_per_device_monthly}</Typography></TableCell>
                  <TableCell>
                    <Chip label={`${t.yearly_discount_pct}%`} size="small"
                      sx={{ bgcolor: t.yearly_discount_pct > 0 ? '#ede9fe' : '#f8fafc', color: t.yearly_discount_pct > 0 ? '#34A8E0' : '#94a3b8', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={t.is_active ? 'Active' : 'Inactive'} size="small"
                      color={t.is_active ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => {
                      setEditingTier(t)
                      setTierForm({ name: t.name, min_devices: t.min_devices, max_devices: t.max_devices ?? '', price_per_device_monthly: t.price_per_device_monthly, yearly_discount_pct: t.yearly_discount_pct, is_active: t.is_active, sort_order: t.sort_order })
                      setTierDialogOpen(true)
                    }}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error"
                      onClick={() => setDeleteTierTarget(t.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </Box>

      {/* Pricing Tier Dialog */}
      <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTier ? 'Edit Pricing Tier' : 'Create Pricing Tier'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Tier Name" value={tierForm.name} onChange={(e) => setTierForm((p) => ({ ...p, name: e.target.value }))} fullWidth placeholder="Starter, Business, Enterprise" />
            <Stack direction="row" spacing={2}>
              <TextField label="Min Devices" type="number" value={tierForm.min_devices} onChange={(e) => setTierForm((p) => ({ ...p, min_devices: Number(e.target.value) }))} fullWidth inputProps={{ min: 1 }} error={tierForm.min_devices < 1} />
              <TextField label="Max Devices" type="number" value={tierForm.max_devices} onChange={(e) => setTierForm((p) => ({ ...p, max_devices: e.target.value === '' ? '' : Number(e.target.value) }))} fullWidth inputProps={{ min: tierForm.min_devices }} error={tierMax !== null && tierMax < tierForm.min_devices} helperText={tierMax !== null && tierMax < tierForm.min_devices ? 'Must be at least the minimum' : 'Leave empty for unlimited'} />
            </Stack>
            <TextField label="Price per Device / Month ($)" type="number" value={tierForm.price_per_device_monthly} onChange={(e) => setTierForm((p) => ({ ...p, price_per_device_monthly: Number(e.target.value) }))} fullWidth inputProps={{ step: '0.01' }} />
            <TextField label="Yearly Discount (%)" type="number" value={tierForm.yearly_discount_pct} onChange={(e) => setTierForm((p) => ({ ...p, yearly_discount_pct: Number(e.target.value) }))} fullWidth inputProps={{ min: 0, max: 100, step: '0.5' }}
              helperText="Percentage discount when customer pays yearly (e.g., 15 = 15% off)" />
            <FormControlLabel control={<Switch checked={tierForm.is_active} onChange={(e) => setTierForm((p) => ({ ...p, is_active: e.target.checked }))} />} label="Active" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTierDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={createTier.isPending || updateTier.isPending || !tierFormValid}
            sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}
            onClick={async () => {
              const data = {
                name: tierForm.name,
                min_devices: tierForm.min_devices,
                max_devices: tierForm.max_devices === '' ? null : Number(tierForm.max_devices),
                price_per_device_monthly: tierForm.price_per_device_monthly,
                yearly_discount_pct: tierForm.yearly_discount_pct,
                is_active: tierForm.is_active,
                sort_order: tierForm.sort_order,
              }
              try {
                if (editingTier) {
                  await updateTier.mutateAsync({ id: editingTier.id, ...data } as PricingTier)
                } else {
                  await createTier.mutateAsync(data as Omit<PricingTier, 'id'>)
                }
                setTierDialogOpen(false)
              } catch { /* API interceptor shows the failure */ }
            }}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTierTarget}
        title="Delete Pricing Tier"
        message="This will remove the pricing tier. Existing subscriptions won't be affected."
        onConfirm={async () => {
          try {
            await deleteTier.mutateAsync(deleteTierTarget!)
            setDeleteTierTarget(null)
          } catch { /* API interceptor shows the failure */ }
        }}
        onClose={() => setDeleteTierTarget(null)}
        loading={deleteTier.isPending}
      />

      {/* ── Payment Gateways ───────────────────────────────────── */}
      <Box sx={{ mt: 3, display: billingSection === 'gateways' ? 'block' : 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Payment sx={{ color: '#34A8E0', fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15 }}>
              Payment Gateways
            </Typography>
          </Box>
          <Button variant="outlined" size="small" startIcon={<Add />} onClick={openCreateGw}
            sx={{ borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#34A8E0', color: '#34A8E0' } }}>
            Add Gateway
          </Button>
        </Box>
        <Card sx={{ bgcolor: '#ffffff', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: '#64748b', fontWeight: 600, fontSize: 12, borderColor: '#f1f5f9', bgcolor: '#f8fafc' } }}>
                <TableCell>Name</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Fee</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {gateways.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#94a3b8', fontSize: 13 }}>
                    No payment gateways configured
                  </TableCell>
                </TableRow>
              )}
              {gateways.map((g) => (
                <TableRow key={g.id} sx={{ '& td': { borderColor: '#f1f5f9' }, '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell><Typography variant="body2" fontWeight={600} color="#1e293b">{g.name}</Typography></TableCell>
                  <TableCell><Chip label={g.provider} size="small" sx={{ fontSize: 11, bgcolor: '#EBF6FD', color: '#34A8E0', border: '1px solid #B3DCF2' }} /></TableCell>
                  <TableCell><Typography variant="body2" color="#475569">{g.currency}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#475569">
                      {g.fee_type === 'percentage' ? `${g.fee_value}%` : `${g.fee_value} ${g.currency}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={g.is_active ? 'active' : 'inactive'} size="small" sx={{
                      fontSize: 11, fontWeight: 600,
                      bgcolor: g.is_active ? '#f0fdf4' : '#f8fafc',
                      color: g.is_active ? '#10b981' : '#94a3b8',
                      border: `1px solid ${g.is_active ? '#bbf7d0' : '#e2e8f0'}`,
                    }} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditGw(g)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#34A8E0', bgcolor: '#EBF6FD' } }}>
                        <Edit sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteGwTarget(g.id)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: '#fff1f2' } }}>
                        <Delete sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </Box>

      {/* ── Tenant Wallets ─────────────────────────────────────── */}
      <Box sx={{ mt: 3, display: billingSection === 'wallets' ? 'block' : 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountBalanceWallet sx={{ color: '#34A8E0', fontSize: 20 }} />
          <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15 }}>
            Tenant Wallets
          </Typography>
        </Box>
        <Card sx={{ bgcolor: '#ffffff', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: '#64748b', fontWeight: 600, fontSize: 12, borderColor: '#f1f5f9', bgcolor: '#f8fafc' } }}>
                <TableCell>Tenant</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#94a3b8', fontSize: 13 }}>
                    No tenants yet
                  </TableCell>
                </TableRow>
              )}
              {(tenants as Tenant[]).map((tenant) => {
                const w = walletByTenant.get(tenant.id)
                return (
                <TableRow key={tenant.id} sx={{ '& td': { borderColor: '#f1f5f9' }, '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell>
                    <Typography variant="body2" color="#1e293b" fontWeight={600}>{tenant.name}</Typography>
                    <Typography variant="caption" color="#94a3b8" fontFamily="monospace">{tenant.id.slice(0, 8)}…</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} color={w && w.balance > 0 ? '#10b981' : '#94a3b8'}>
                      {w ? w.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'Not initialized'}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2" color="#475569">{w?.currency || tenant.currency || 'USD'}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="#94a3b8">{w ? new Date(w.updated_at).toLocaleDateString() : '—'}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="View transactions">
                      <span>
                      <IconButton size="small" disabled={!w} onClick={() => setHistoryTenantId(tenant.id)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#34A8E0', bgcolor: '#EBF6FD' } }}>
                        <History sx={{ fontSize: 14 }} />
                      </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Top up wallet">
                      <IconButton size="small" onClick={() => openTopup(tenant.id)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#10b981', bgcolor: '#f0fdf4' } }}>
                        <AttachMoney sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </Card>
      </Box>

      {/* Gateway create/edit dialog */}
      <Dialog open={gwDialogOpen} onClose={() => setGwDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', pb: 1 }}>
          {editingGw ? 'Edit Gateway' : 'Add Payment Gateway'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Name" value={gwForm.name} required fullWidth size="small" autoFocus
            onChange={(e) => setGwForm((p) => ({ ...p, name: e.target.value }))} />
          <TextField select label="Provider" value={gwForm.provider} required fullWidth size="small"
            helperText="Payment flow implemented by the billing service"
            onChange={(e) => setGwForm((p) => ({ ...p, provider: e.target.value }))}>
            <MenuItem value="qi_card">Qi Card</MenuItem>
            <MenuItem value="zaincash">ZainCash</MenuItem>
            <MenuItem value="hyperpay">HyperPay</MenuItem>
          </TextField>
          <TextField select label="Currency" value={gwForm.currency} fullWidth size="small"
            onChange={(e) => setGwForm((p) => ({ ...p, currency: e.target.value }))}>
            {['USD', 'EUR', 'IQD'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField select label="Fee Type" value={gwForm.fee_type} size="small" sx={{ flex: 1 }}
              onChange={(e) => setGwForm((p) => ({ ...p, fee_type: e.target.value as 'percentage' | 'fixed' }))}>
              <MenuItem value="percentage">Percentage (%)</MenuItem>
              <MenuItem value="fixed">Fixed amount</MenuItem>
            </TextField>
            <TextField label={gwForm.fee_type === 'percentage' ? 'Fee %' : 'Fee amount'} type="number"
              value={gwForm.fee_value} size="small" sx={{ flex: 1 }}
              inputProps={{ min: 0, step: 0.01 }}
              onChange={(e) => setGwForm((p) => ({ ...p, fee_value: parseFloat(e.target.value) || 0 }))} />
          </Box>
          <FormControlLabel
            control={<Switch checked={gwForm.is_active} onChange={(e) => setGwForm((p) => ({ ...p, is_active: e.target.checked }))} />}
            label={<Typography variant="body2" color="#475569">Active</Typography>}
          />
          <TextField label="Configuration (JSON)" value={gwForm.config} fullWidth size="small" multiline minRows={2}
            error={!!gatewayConfigError}
            helperText={gatewayConfigError || 'Provider-specific non-secret settings'}
            onChange={(e) => setGwForm((p) => ({ ...p, config: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setGwDialogOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleGwSubmit}
            disabled={createGateway.isPending || updateGateway.isPending || !gwForm.name.trim() || !gwForm.provider || !!gatewayConfigError}
            sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)', borderRadius: 1.5, fontWeight: 600 }}>
            {editingGw ? 'Update' : 'Add Gateway'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Topup dialog */}
      <Dialog open={topupDialogOpen} onClose={() => setTopupDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', pb: 1 }}>Top Up Wallet</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Amount" type="number" value={topupForm.amount} required fullWidth size="small" autoFocus
            inputProps={{ min: 0.01, step: 0.01 }}
            onChange={(e) => setTopupForm((p) => ({ ...p, amount: e.target.value }))} />
          <TextField label="Description" value={topupForm.description} fullWidth size="small"
            placeholder="e.g. Manual topup via bank transfer"
            onChange={(e) => setTopupForm((p) => ({ ...p, description: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTopupDialogOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleTopup}
            disabled={topupWallet.isPending || !topupForm.amount || parseFloat(topupForm.amount) <= 0}
            sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)', borderRadius: 1.5, fontWeight: 600 }}>
            Top Up
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteGwTarget}
        title="Delete Gateway"
        message="This will permanently delete this payment gateway."
        onConfirm={async () => {
          try {
            await deleteGateway.mutateAsync(deleteGwTarget!)
            setDeleteGwTarget(null)
          } catch { /* API interceptor shows the failure */ }
        }}
        onClose={() => setDeleteGwTarget(null)}
        loading={deleteGateway.isPending}
      />

      {/* ── Payment Transactions (all tenants) ──────────────────── */}
      <Box sx={{ mt: 3, display: billingSection === 'transactions' ? 'block' : 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Payment sx={{ color: '#34A8E0', fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15 }}>
              Gateway Transactions
            </Typography>
            <Chip label={`${adminPayments.length}`} size="small" sx={{ bgcolor: '#EBF6FD', color: '#34A8E0', fontWeight: 600 }} />
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => {
            setSubTenantForm({ tenant_id: '', device_count: '5', billing_cycle: 'monthly', amount: '', description: '' })
            setSubTenantDialog(true)
          }} sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>
            Subscribe Tenant
          </Button>
        </Box>
        <Card sx={{ bgcolor: '#ffffff', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: '#64748b', fontWeight: 600, fontSize: 12, borderColor: '#f1f5f9', bgcolor: '#f8fafc' } }}>
                <TableCell>Date</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gateway</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminPayments.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: '#94a3b8' }}>No transactions yet</TableCell></TableRow>
              )}
              {adminPayments.slice(0, 50).map((p) => {
                const tenantName = (tenants as Tenant[]).find((t: Tenant) => t.id === p.tenant_id)?.name ?? p.tenant_id.slice(0, 8)
                const gwName = gateways.find((g) => g.id === p.gateway_id)?.name ?? '—'
                return (
                  <TableRow key={p.id} sx={{ '& td': { borderColor: '#f1f5f9' }, '&:hover': { bgcolor: '#f8fafc' } }}>
                    <TableCell><Typography variant="caption" color="#64748b" fontFamily="monospace">{new Date(p.created_at).toLocaleString()}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600} color="#1e293b">{tenantName}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={700} color="#0f172a">{p.amount?.toFixed(2)}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="#475569">{p.currency}</Typography></TableCell>
                    <TableCell>
                      <Chip label={p.status} size="small" sx={{
                        fontSize: 10, fontWeight: 600,
                        bgcolor: p.status === 'completed' ? '#f0fdf4' : p.status === 'pending' ? '#eff6ff' : '#fff1f2',
                        color: p.status === 'completed' ? '#10b981' : p.status === 'pending' ? '#3b82f6' : '#ef4444',
                        border: `1px solid ${p.status === 'completed' ? '#bbf7d0' : p.status === 'pending' ? '#bfdbfe' : '#fecaca'}`,
                      }} />
                    </TableCell>
                    <TableCell><Typography variant="caption" color="#94a3b8">{gwName}</Typography></TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      </Box>

      {/* Subscribe Tenant Dialog (admin manual subscription — no payment gateway needed) */}
      <Dialog open={subTenantDialog} onClose={() => setSubTenantDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>Subscribe Tenant (Manual)</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            This subscribes a tenant directly without requiring them to pay through a gateway. Use this when you've collected payment offline.
          </Alert>
          <TextField select label="Tenant" value={subTenantForm.tenant_id} required fullWidth size="small"
            onChange={(e) => setSubTenantForm((p) => ({ ...p, tenant_id: e.target.value }))}>
            {(tenants as Tenant[]).map((t: Tenant) => (
              <MenuItem key={t.id} value={t.id}>{t.name} ({t.id.slice(0, 8)})</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Device Count" type="number" value={subTenantForm.device_count} required size="small" sx={{ flex: 1 }}
              inputProps={{ min: 1 }}
              onChange={(e) => setSubTenantForm((p) => ({ ...p, device_count: e.target.value }))} />
            <TextField select label="Billing Cycle" value={subTenantForm.billing_cycle} required size="small" sx={{ flex: 1 }}
              onChange={(e) => setSubTenantForm((p) => ({ ...p, billing_cycle: e.target.value }))}>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
          </Box>
          <TextField label="Amount Override (optional)" type="number" value={subTenantForm.amount} size="small" fullWidth
            inputProps={{ min: 0, step: '0.01' }}
            helperText="Leave empty to calculate from pricing tiers. Overrides must be greater than zero."
            onChange={(e) => setSubTenantForm((p) => ({ ...p, amount: e.target.value }))} />
          <TextField label="Description / Notes" value={subTenantForm.description} size="small" fullWidth
            placeholder="e.g. Paid via bank transfer, Invoice #1234"
            onChange={(e) => setSubTenantForm((p) => ({ ...p, description: e.target.value }))} />
          {subscribeTenant.isError && <Alert severity="error">Failed to subscribe tenant</Alert>}
          {subscribeTenant.isSuccess && <Alert severity="success">Tenant subscribed successfully!</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSubTenantDialog(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button variant="contained"
            disabled={
              subscribeTenant.isPending ||
              !subTenantForm.tenant_id ||
              Number(subTenantForm.device_count) < 1 ||
              (subTenantForm.amount !== '' && Number(subTenantForm.amount) <= 0)
            }
            onClick={async () => {
              try {
                await subscribeTenant.mutateAsync({
                  tenant_id: subTenantForm.tenant_id,
                  device_count: parseInt(subTenantForm.device_count),
                  billing_cycle: subTenantForm.billing_cycle,
                  amount: subTenantForm.amount ? parseFloat(subTenantForm.amount) : undefined,
                  description: subTenantForm.description || undefined,
                })
                setSubTenantDialog(false)
              } catch { /* API interceptor shows the failure */ }
            }}
            sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', borderRadius: 1.5, fontWeight: 600 }}>
            {subscribeTenant.isPending ? 'Subscribing...' : 'Subscribe Tenant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction history dialog */}
      <Dialog open={!!historyTenantId} onClose={() => setHistoryTenantId(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', pb: 1 }}>
          Wallet Transactions
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: '#64748b', fontWeight: 600, fontSize: 12, borderColor: '#f1f5f9', bgcolor: '#f8fafc' } }}>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Gateway</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {txHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#94a3b8', fontSize: 13 }}>
                    No transactions yet
                  </TableCell>
                </TableRow>
              )}
              {txHistory.map((t) => (
                <TableRow key={t.id} sx={{ '& td': { borderColor: '#f1f5f9' } }}>
                  <TableCell>
                    <Typography variant="caption" color="#64748b" fontFamily="monospace">
                      {new Date(t.created_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={t.type} size="small" sx={{
                      fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                      bgcolor: t.type === 'topup' ? '#f0fdf4' : '#fff1f2',
                      color: t.type === 'topup' ? '#10b981' : '#ef4444',
                      border: `1px solid ${t.type === 'topup' ? '#bbf7d0' : '#fecaca'}`,
                    }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} color={t.type === 'topup' ? '#10b981' : '#ef4444'}>
                      {t.type === 'topup' ? '+' : '-'}{Math.abs(t.amount).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#475569">{t.description || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="#94a3b8" fontFamily="monospace">
                      {t.gateway_id ? t.gateway_id.slice(0, 8) + '…' : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setHistoryTenantId(null)} sx={{ color: '#64748b' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
